import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Input,
    ListGroup,
    ListGroupItem,
    Container,
    Row,
    Col,
    Button,
    Pagination,
    PaginationItem,
    Table,
    PaginationLink,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Card,
    CardBody,
    Label,
    FormGroup,
    Badge
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { productActions } from '../../sagas/productSlice';
import { debounce } from 'lodash';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaCalendarAlt, FaEnvelope, FaHistory, FaMapMarkerAlt, FaMinus, FaMoneyBillWave, FaPlus, FaPrint, FaReceipt, FaSearch, FaShoppingCart, FaStore, FaTrash, FaUndoAlt, FaUser, FaWhatsapp } from 'react-icons/fa'; // Import trash icon
import { refundActions } from '../../sagas/refundSlice';
import { useNavigate } from 'react-router-dom';

const RefundForm = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [refundItems, setRefundItems] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [criteria, setSearchCriteria] = useState({ query: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef(null);

    const {
        products,
        loading,
        totalPages,
        totalElements,
        pageNumber,
        pageSize,
        error,
    } = useSelector((state) => state.products);

    useEffect(() => {
        let payload = {
            criteria,
            page: currentPage,
            size: 10,
        };
        dispatch(productActions.search(payload));
    }, [currentPage]);

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [products]);

    const debouncedSearch = useCallback(
        debounce((query) => {
            setIsSearching(true);
            dispatch(productActions.search({
                criteria: { query },
                page: 1,
                size: 10,
                onSuccess: () => setIsSearching(false)
            }));
        }, 500), // Wait 500ms after last keystroke
        []
    );

    const formatPrice = (amount) => {
        const formattedPrice = new Intl.NumberFormat('fr-MA', {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
        return `${formattedPrice} DH`;
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    const handleAddProduct = (product) => {
        const existingItem = refundItems.find(item => item.productId === product.id);
        if (existingItem) {
            handleUpdateQuantity(existingItem.productId, existingItem.quantity + 1);
        } else {
            setRefundItems([...refundItems, {
                productId: product.id,
                productDescription: product.description,
                price: product.userPrice,
                quantity: 1,
                total: product.userPrice
            }]);
        }
    };

    const handleUpdateQuantity = (productId, newQuantity) => {
        if (newQuantity < 1) return;
        setRefundItems(refundItems.map(item =>
            item.productId === productId
                ? { ...item, quantity: newQuantity, total: item.price * newQuantity }
                : item
        ));
    };

    const handleRemoveItem = (productId) => {
        setRefundItems(refundItems.filter(item => item.productId !== productId));
    };

    const handleSubmit = async () => {
        try {
            dispatch(refundActions.addRefund(
                {
                    data: { items: refundItems, total: totalAmount },
                    onSuccess: () => {
                        setRefundItems([]);
                        setTotalAmount(0);
                        Swal.fire({
                            title: t('refund.successTitle'),
                            text: t('refund.successMessage'),
                            icon: 'success'
                        });

                    }
                }
            ));



            // Generate and print bill
            //handlePrintBill(result.data);
        } catch (error) {
            Swal.fire({
                title: t('refund.errorTitle'),
                text: error.message,
                icon: 'error'
            });
        }
    };

    useEffect(() => {
        const total = refundItems.reduce((sum, item) => sum + item.total, 0);
        setTotalAmount(total);
    }, [refundItems]);

    return (
        <Container fluid>
            <Row>
                <Col md={7} className="pe-4">
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                {/* Left side - Title */}
                                <h4 className="mb-0">
                                    <FaUndoAlt className="text-primary me-2" />
                                    {t('nav.refund')}
                                </h4>
                                <Button
                                    color="primary"
                                    className="d-flex align-items-center"
                                    onClick={() => navigate('/refund/history')}
                                    title={t('refund.viewHistory')}
                                >
                                    <FaHistory className="me-2" />
                                    {t('refund.viewHistory')}
                                </Button>
                            </div>

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t('common.loading')}</span>
                                    </div>
                                    <div className="mt-2 text-muted">
                                        {t('common.loadingData')}
                                    </div>
                                </div>
                            ) : (<>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    {/* Search Input - Left Side */}
                                    <div className="position-relative flex-grow-1 me-3">
                                        <Input
                                            innerRef={searchInputRef}
                                            type="text"
                                            placeholder={t('product.searchFocusHint')}
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                            className={`ps-4 pe-5 ${isSearching ? 'searching' : ''}`}
                                            title={t('product.searchShortcut')}
                                        />
                                        <FaSearch
                                            style={{
                                                position: 'absolute',
                                                left: '10px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: '#adb5bd'
                                            }}
                                            size={14}
                                        />
                                        <FaBarcode
                                            style={{
                                                position: 'absolute',
                                                right: '10px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: '#adb5bd'
                                            }}
                                            size={20}
                                            title={t('product.scanHint')}
                                        />
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <Table bordered hover>
                                        <thead>
                                            <tr>
                                                <th>{t('product.code')}</th>
                                                <th>{t('product.description')}</th>
                                                <th>{t('product.salePrice')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!products || products.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-4">
                                                        <div className="text-muted">
                                                            {t('product.noProducts')}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (products.map((product) => (
                                                <tr key={product.id} onClick={() => handleAddProduct(product)}>
                                                    <td>{product.code}</td>
                                                    <td>{product.description}</td>
                                                    <td className="text-end">{formatPrice(product.userPrice)}</td>
                                                </tr>
                                            ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <small className="text-muted">
                                        {t('product.showing', {
                                            start: (currentPage - 1) * pageSize + 1,
                                            end: Math.min(currentPage * pageSize, totalElements),
                                            total: totalElements,
                                            currentPage: currentPage,
                                            totalPages: totalPages
                                        })}
                                    </small>
                                    <Pagination size="sm" className="mb-0">
                                        <PaginationItem disabled={currentPage === 1}>
                                            <PaginationLink first onClick={() => handlePageChange(1)}>
                                                <FaAngleDoubleLeft size={12} />
                                            </PaginationLink>
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPage === 1}>
                                            <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)}>
                                                <FaAngleLeft size={12} />
                                            </PaginationLink>
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPage === totalPages}>
                                            <PaginationLink next onClick={() => handlePageChange(currentPage + 1)}>
                                                <FaAngleRight size={12} />
                                            </PaginationLink>
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPage === totalPages}>
                                            <PaginationLink last onClick={() => handlePageChange(totalPages)}>
                                                <FaAngleDoubleRight size={12} />
                                            </PaginationLink>
                                        </PaginationItem>
                                    </Pagination>
                                </div>
                            </>)}
                        </CardBody>
                    </Card>
                </Col>
                <Col md={5}>
                    <Card>
                        <CardBody>
                            <h5>{t('refund.items')}</h5>
                            <div className="cart-container" style={{
                                height: '500px',
                                overflowY: 'auto'
                            }}>
                                <Table hover responsive className="mb-0 cart-table">
                                    <thead>
                                        <tr>
                                            <th>{t('product.description')}</th>
                                            <th>{t('refund.quantity')}</th>
                                            <th>{t('refund.total')}</th>
                                            <th>{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {refundItems.map(item => (
                                            <tr key={item.productId}>
                                                <td>{item.productDescription}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Button size="sm" onClick={() => handleUpdateQuantity(item.productId, item.quantity - 1)}>
                                                            <FaMinus />
                                                        </Button>
                                                        <Input
                                                            type="number"
                                                            onFocus={(e) => e.target.select()} // Select the field content on focus
                                                            value={item.quantity}
                                                            onChange={(e) => handleUpdateQuantity(item.productId, parseInt(e.target.value))}
                                                            className="mx-2"
                                                            style={{ width: '60px' }}
                                                        />
                                                        <Button size="sm" onClick={() => handleUpdateQuantity(item.productId, item.quantity + 1)}>
                                                            <FaPlus />
                                                        </Button>
                                                    </div>
                                                </td>
                                                <td>{formatPrice(item.total)}</td>
                                                <td>
                                                    <Button color="danger" size="sm" onClick={() => handleRemoveItem(item.productId)}>
                                                        <FaTrash />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <h5>{t('refund.total')}: {formatPrice(totalAmount)}</h5>
                                <Button
                                    color="primary"
                                    disabled={refundItems.length === 0}
                                    onClick={handleSubmit}
                                >
                                    {t('refund.submit')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default RefundForm;